import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useEvent } from '../store';
import { loadEvent as loadEventFromStorage } from '../utils';
import { Leaderboard } from '../components/leaderboard';

/**
 * Event Leaderboard Page
 * Shows leaderboard for a specific event
 */
export function EventLeaderboardPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, leaderboard } = useEvent();
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) {
      navigate('/leaderboard');
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      try {
        const eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          loadEvent(eventData);
        } else {
          navigate('/leaderboard');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate('/leaderboard');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, loadEvent, navigate]);

  const handleUserClick = (participantId: string) => {
    navigate(`/leaderboard/${eventId}/user/${participantId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">Event Not Found</h1>
            <Button variant="secondary" onClick={() => navigate('/leaderboard')}>
              Back to Global Leaderboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">{event.name}</h1>
            <p className="text-sm text-secondary-text">Event Leaderboard</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/leaderboard')}
            >
              Global Leaderboard
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/event/${eventId}`)}
            >
              View Event
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Event Info */}
        <Card padding="md">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-secondary-text mb-1">Status</div>
              <Badge
                variant={
                  event.finished
                    ? 'default'
                    : event.status === 'live'
                    ? 'error'
                    : 'info'
                }
                size="sm"
              >
                {event.finished ? 'Finished' : event.status}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-secondary-text mb-1">Participants</div>
              <div className="font-semibold text-primary-text">
                {event.participants.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-secondary-text mb-1">Fights</div>
              <div className="font-semibold text-primary-text">
                {event.fights.length}
              </div>
            </div>
          </div>
        </Card>

        {/* Leaderboard */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-primary-text mb-4">Rankings</h2>
          <Leaderboard
            entries={leaderboard}
            onUserClick={handleUserClick}
          />
        </Card>
      </div>
    </div>
  );
}
